import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { Navigate, useParams } from "react-router-dom";
import { ChartLine, TriangleAlert } from "lucide-react";
import type { HomeCommand, HomeState, MiniSplitFanSpeed, MiniSplitMode, ThermostatMode } from "@home-control/shared";
import { fetchClimateHistory, type ClimateHistory, type HistoryRange } from "../../api";
import { useHome } from "../state";
import { SectionHeader } from "../components/SectionHeader";
import { Segmented } from "../components/Segmented";
import { Spinner } from "../components/Spinner";
import { StatusPill } from "../components/StatusPill";
import { TemperatureDial } from "../components/TemperatureDial";
import { clamp, findRuns, formatHour, formatMonthDay, formatTooltipDate, useContainerWidth } from "./chart";
import "./climate.css";

type ZoneId = "hallway" | "bedroom" | "mini-split";

const thermostatModeOptions: { value: ThermostatMode; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "heat", label: "Heat" },
  { value: "cool", label: "Cool" },
  { value: "heatCool", label: "Auto" }
];

const miniSplitModeOptions: { value: MiniSplitMode; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "heat", label: "Heat" },
  { value: "cool", label: "Cool" },
  { value: "auto", label: "Auto" },
  { value: "dry", label: "Dry" },
  { value: "fan", label: "Fan" }
];

const fanOptions: { value: MiniSplitFanSpeed; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" }
];

interface ZoneView {
  id: ZoneId;
  label: string;
  subtitle: string;
  online: boolean;
  currentTempF: number | null;
  targetTempF: number | null;
  humidityPercent: number | null;
  fan: string | null;
  min: number;
  max: number;
  tint: string;
  statusText: string;
  modeValue: string;
  modeLabel: string;
  modeOptions: { value: string; label: string }[];
  targetCommand: (targetTempF: number) => HomeCommand;
  modeCommand: (mode: string) => HomeCommand;
}

/** Zone tint logic (spec §2.2): the live action wins, then the mode. */
function zoneTint(action: string, mode: string): string {
  if (action === "heating") return "var(--warm)";
  if (action === "cooling") return "var(--cool)";
  if (mode === "heat") return "var(--warm)";
  if (mode === "off") return "var(--muted)";
  return "var(--cool)";
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

function thermostatStatusText(action: string): string {
  switch (action) {
    case "cooling":
      return "Cool";
    case "heating":
      return "Heat";
    case "fan":
      return "Fan";
    case "off":
      return "Idle";
    default:
      return capitalize(action);
  }
}

function buildZone(state: HomeState, id: ZoneId): ZoneView {
  if (id === "mini-split") {
    const zone = state.miniSplit;
    const modeLabel =
      miniSplitModeOptions.find((option) => option.value === zone.mode)?.label ?? capitalize(zone.mode);
    return {
      id,
      label: zone.label,
      subtitle: "Office only",
      online: zone.online,
      currentTempF: zone.currentTempF,
      targetTempF: zone.targetTempF,
      humidityPercent: null,
      fan: zone.fan,
      min: 61,
      max: 88,
      tint: zoneTint(zone.action, zone.mode),
      statusText: capitalize(zone.action),
      modeValue: zone.mode,
      modeLabel,
      modeOptions: miniSplitModeOptions,
      targetCommand: (targetTempF) => ({ kind: "setMiniSplitTarget", targetTempF }),
      modeCommand: (mode) => ({ kind: "setMiniSplitMode", mode: mode as MiniSplitMode })
    };
  }

  const zone = state.thermostats[id];
  const modeLabel =
    thermostatModeOptions.find((option) => option.value === zone.mode)?.label ?? capitalize(zone.mode);
  return {
    id,
    label: zone.label,
    subtitle: "House system",
    online: zone.online,
    currentTempF: zone.currentTempF,
    targetTempF: zone.targetTempF,
    humidityPercent: zone.humidityPercent,
    fan: null,
    min: 50,
    max: 90,
    tint: zoneTint(zone.action, zone.mode),
    statusText: thermostatStatusText(zone.action),
    modeValue: zone.mode,
    modeLabel,
    modeOptions: thermostatModeOptions,
    targetCommand: (targetTempF) => ({ kind: "setThermostatTarget", thermostat: id, targetTempF }),
    modeCommand: (mode) => ({ kind: "setThermostatMode", thermostat: id, mode: mode as ThermostatMode })
  };
}

export function ClimatePage() {
  const { zoneId } = useParams<{ zoneId: string }>();
  if (zoneId !== "hallway" && zoneId !== "bedroom" && zoneId !== "mini-split") {
    return <Navigate to="/" replace />;
  }
  return <ClimateDetail zoneId={zoneId} />;
}

function ClimateDetail({ zoneId }: { zoneId: ZoneId }) {
  const { state, token, command, isPending, canControlDevices } = useHome();
  const [range, setRange] = useState<HistoryRange>("24h");
  const [history, setHistory] = useState<{
    status: "loading" | "ready" | "error";
    data?: ClimateHistory;
    message?: string;
  }>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setHistory({ status: "loading" });
    fetchClimateHistory(token, zoneId, range)
      .then((data) => {
        if (!cancelled) setHistory({ status: "ready", data });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setHistory({
            status: "error",
            message: error instanceof Error && error.message ? error.message : "History read failed"
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, zoneId, range]);

  if (!state) {
    return (
      <main className="page climate-page">
        <SectionHeader title="Climate" />
        <p className="placeholder-note">Syncing latest climate data.</p>
      </main>
    );
  }

  const zone = buildZone(state, zoneId);
  const gated = !canControlDevices;
  const targetPending = isPending(`${zoneId}-target`);
  const modePending = isPending(`${zoneId}-mode`);
  const fanPending = isPending("mini-split-fan");
  const dialFallback = Math.round((zone.min + zone.max) / 2);

  return (
    <main className="page climate-page">
      <SectionHeader title={zone.label} />

      <section className="cl-header">
        <div className="cl-header-top">
          <div className="cl-header-names">
            <h2 className="cl-zone-name">{zone.label}</h2>
            <p className="cl-zone-sub">{zone.subtitle}</p>
          </div>
          <StatusPill text={zone.statusText} tint={zone.tint} />
        </div>
        <div className="cl-metrics">
          <Metric value={`${zone.currentTempF ?? "--"}°`} caption="Current" />
          <Metric value={`${zone.targetTempF ?? "--"}°`} caption="Target" />
          <Metric value={zone.humidityPercent === null ? "--" : `${zone.humidityPercent}%`} caption="Humidity" />
        </div>
      </section>

      <TemperatureDial
        label={`${zone.label} target temperature`}
        value={zone.targetTempF ?? dialFallback}
        min={zone.min}
        max={zone.max}
        tint={zone.tint}
        disabled={gated}
        pending={targetPending}
        onCommit={(value) => void command(`${zoneId}-target`, zone.targetCommand(value))}
      />

      <section className={`cl-panel${gated ? " gated" : ""}`}>
        <div className="cl-panel-head">
          <h2 className="cl-panel-title">Mode</h2>
          {modePending ? <Spinner size={16} /> : null}
        </div>
        <Segmented
          options={zone.modeOptions}
          value={zone.modeValue}
          tint={zone.tint}
          disabled={gated || modePending}
          onChange={(mode) => void command(`${zoneId}-mode`, zone.modeCommand(mode))}
        />
      </section>

      {zoneId === "mini-split" ? (
        <section className={`cl-panel${gated ? " gated" : ""}`}>
          <div className="cl-panel-head">
            <h2 className="cl-panel-title">Fan</h2>
            {fanPending ? <Spinner size={16} /> : null}
          </div>
          <Segmented
            options={fanOptions}
            value={(zone.fan as MiniSplitFanSpeed | null) ?? null}
            tint="var(--cool)"
            disabled={gated || fanPending}
            onChange={(fan) => void command("mini-split-fan", { kind: "setMiniSplitFan", fan })}
          />
        </section>
      ) : null}

      <section className="cl-panel">
        <div className="cl-panel-head">
          <h2 className="cl-panel-title">History</h2>
          <div className="range-picker" role="group" aria-label="History range">
            {(["24h", "7d"] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={`range-option${range === option ? " selected" : ""}`}
                aria-pressed={range === option}
                onClick={() => setRange(option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <HistoryContent history={history} tint={zone.tint} range={range} />
      </section>

      <section className="cl-panel">
        <div className="cl-panel-head">
          <h2 className="cl-panel-title">Status</h2>
        </div>
        <div className="cl-status-rows">
          <StatusRow
            label="Connection"
            value={zone.online ? "Online" : "Offline"}
            tint={zone.online ? "var(--cool)" : "var(--warm)"}
          />
          <StatusRow label="Mode" value={zone.modeLabel} tint={zone.tint} />
          {zoneId === "mini-split" ? (
            <StatusRow label="Fan" value={zone.fan ? capitalize(zone.fan) : "--"} tint="var(--muted)" />
          ) : (
            <StatusRow
              label="Humidity"
              value={zone.humidityPercent === null ? "--" : `${zone.humidityPercent}%`}
              tint="var(--muted)"
            />
          )}
        </div>
      </section>
    </main>
  );
}

function Metric({ value, caption }: { value: string; caption: string }) {
  return (
    <div className="cl-metric">
      <span className="cl-metric-value">{value}</span>
      <span className="cl-metric-caption">{caption}</span>
    </div>
  );
}

function StatusRow({ label, value, tint }: { label: string; value: string; tint: string }) {
  return (
    <div className="cl-status-row">
      <span className="cl-status-label">{label}</span>
      <span className="cl-status-value" style={{ color: tint }}>
        {value}
      </span>
    </div>
  );
}

interface DrawPoint {
  time: Date;
  temp: number;
  target: number | null;
  coolingOn: number;
  heatingOn: number;
  runtimeOn: number;
}

function HistoryContent({
  history,
  tint,
  range
}: {
  history: { status: "loading" | "ready" | "error"; data?: ClimateHistory; message?: string };
  tint: string;
  range: HistoryRange;
}) {
  const points = useMemo<DrawPoint[]>(() => {
    if (!history.data) return [];
    return history.data.points
      .filter((point) => point.temperatureF !== null)
      .map((point) => ({
        time: new Date(point.timestamp),
        temp: point.temperatureF as number,
        target: point.targetTemperatureF,
        coolingOn: point.coolingOn,
        heatingOn: point.heatingOn,
        runtimeOn: point.runtimeOn
      }))
      .filter((point) => !Number.isNaN(point.time.getTime()));
  }, [history.data]);

  if (history.status === "loading") {
    return (
      <div className="cl-chart-state centered">
        <Spinner size={20} />
        <span className="cl-chart-state-text">Loading history</span>
      </div>
    );
  }
  if (history.status === "error") {
    return (
      <div className="cl-chart-state">
        <TriangleAlert size={24} strokeWidth={2.4} style={{ color: "var(--warm)" }} />
        <span className="cl-chart-empty-title">History unavailable</span>
        <span className="cl-chart-state-text">{history.message}</span>
      </div>
    );
  }
  if (points.length === 0) {
    return (
      <div className="cl-chart-state">
        <ChartLine size={24} strokeWidth={2.4} style={{ color: tint }} />
        <span className="cl-chart-empty-title">No history yet</span>
        <span className="cl-chart-state-text">Real data will appear after AWS records climate metrics.</span>
      </div>
    );
  }
  return (
    <>
      <ClimateChart key={range} points={points} tint={tint} />
      <div className="chart-legend">
        <LegendItem color={tint} label="Room temp" />
        <LegendItem color="var(--target-line)" label="Target" />
        <LegendItem color="color-mix(in srgb, var(--cool) 72%, transparent)" label="AC on" />
      </div>
    </>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="chart-legend-item">
      <span className="chart-legend-dot" style={{ background: color }} />
      {label}
    </span>
  );
}

const chartHeight = 170;
const inset = { top: 12, left: 36, bottom: 26, right: 8 };

function runtimeLabel(point: DrawPoint): string {
  if (point.coolingOn > 0.05) return "AC on";
  if (point.heatingOn > 0.05) return "Heat on";
  if (point.runtimeOn > 0.05) return "Running";
  return "Idle";
}

function ClimateChart({ points, tint }: { points: DrawPoint[]; tint: string }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [selected, setSelected] = useState<number | null>(null);

  const count = points.length;
  const plotWidth = Math.max(1, width - inset.left - inset.right);
  const plotHeight = chartHeight - inset.top - inset.bottom;
  const plotBottom = inset.top + plotHeight;

  const values = points.flatMap((point) => (point.target === null ? [point.temp] : [point.temp, point.target]));
  const lower = Math.floor((Math.min(...values) - 1) / 2) * 2;
  const upper = Math.max(Math.ceil((Math.max(...values) + 1) / 2) * 2, lower + 4);

  const x = (index: number) => inset.left + (count === 1 ? plotWidth / 2 : (index / (count - 1)) * plotWidth);
  const y = (value: number) => plotBottom - ((value - lower) / (upper - lower)) * plotHeight;

  const runs = findRuns(count, (index) => points[index].runtimeOn > 0.05);
  const halfStep = count > 1 ? plotWidth / (count - 1) / 2 : plotWidth / 2;

  // Downsample the temperature line when dense (spec §3.5).
  const cap = Math.max(2, Math.floor(plotWidth * 1.5));
  let lineIndexes: number[];
  if (count <= cap) {
    lineIndexes = points.map((_, index) => index);
  } else {
    const stride = Math.ceil(count / cap);
    lineIndexes = [];
    for (let i = 0; i < count; i += stride) lineIndexes.push(i);
    if (lineIndexes[lineIndexes.length - 1] !== count - 1) lineIndexes.push(count - 1);
  }
  const tempPath = lineIndexes
    .map((index, order) => `${order === 0 ? "M" : "L"} ${x(index).toFixed(2)} ${y(points[index].temp).toFixed(2)}`)
    .join(" ");

  let targetPath = "";
  let penDown = false;
  points.forEach((point, index) => {
    if (point.target === null) {
      penDown = false;
      return;
    }
    targetPath += `${penDown ? "L" : "M"} ${x(index).toFixed(2)} ${y(point.target).toFixed(2)} `;
    penDown = true;
  });

  const spanHours = count > 1 ? (points[count - 1].time.getTime() - points[0].time.getTime()) / 3_600_000 : 0;
  const formatAxis = (date: Date) => (spanHours <= 36 ? formatHour(date) : formatMonthDay(date));
  const axisIndexes = count === 1 ? [0] : [0, Math.floor((count - 1) / 2), count - 1];

  function scrub(event: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el || count === 0) return;
    const rect = el.getBoundingClientRect();
    const fraction = clamp((event.clientX - rect.left - inset.left) / plotWidth, 0, 1);
    setSelected(Math.round(fraction * (count - 1)));
  }

  const selectedPoint = selected === null ? null : points[selected];
  const tooltipX = selected === null ? 0 : clamp(x(selected), 96, Math.max(96, width - 96));

  return (
    <div
      ref={ref}
      className="chart-surface"
      style={{ height: chartHeight }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        scrub(event);
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) scrub(event);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
    >
      {width > 0 ? (
        <svg width={width} height={chartHeight} aria-hidden="true">
          {[0, 1, 2, 3].map((step) => {
            const value = lower + ((upper - lower) * step) / 3;
            const lineY = y(value);
            return (
              <g key={step}>
                <line
                  x1={inset.left}
                  x2={width - inset.right}
                  y1={lineY}
                  y2={lineY}
                  stroke="var(--stroke)"
                  strokeWidth={1}
                  opacity={step === 0 ? 1 : 0.82}
                />
                <text className="chart-axis-label" x={inset.left - 8} y={lineY + 3} textAnchor="end">
                  {Math.round(value)}°
                </text>
              </g>
            );
          })}

          <rect
            x={inset.left}
            y={plotBottom - 8}
            width={plotWidth}
            height={8}
            rx={3}
            fill="color-mix(in srgb, var(--cool) 12%, transparent)"
          />
          {runs.map(([start, end]) => {
            const x0 = clamp(x(start) - halfStep, inset.left, inset.left + plotWidth);
            const x1 = clamp(x(end) + halfStep, inset.left, inset.left + plotWidth);
            return (
              <rect
                key={`run-${start}`}
                x={x0}
                y={plotBottom - 30}
                width={Math.max(2, x1 - x0)}
                height={30}
                rx={3}
                fill="color-mix(in srgb, var(--cool) 78%, transparent)"
              />
            );
          })}

          {targetPath ? (
            <path d={targetPath} fill="none" stroke="var(--target-line)" strokeWidth={1.5} strokeDasharray="5 6" />
          ) : null}
          <path
            d={tempPath}
            fill="none"
            stroke={tint}
            strokeWidth={2.25}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {selected === null ? (
            <circle
              cx={x(count - 1)}
              cy={y(points[count - 1].temp)}
              r={4}
              fill="var(--surface)"
              stroke={tint}
              strokeWidth={2}
            />
          ) : null}

          {selected !== null && selectedPoint ? (
            <g>
              <line
                x1={x(selected)}
                x2={x(selected)}
                y1={inset.top}
                y2={plotBottom}
                stroke="color-mix(in srgb, var(--ink) 28%, transparent)"
                strokeWidth={1}
                strokeDasharray="4 4"
              />
              <circle
                cx={x(selected)}
                cy={y(selectedPoint.temp)}
                r={5}
                fill="var(--surface)"
                stroke={tint}
                strokeWidth={2.25}
              />
            </g>
          ) : null}

          {axisIndexes.map((index, order) => (
            <text
              key={`axis-${index}`}
              className="chart-axis-label"
              x={x(index)}
              y={plotBottom + 18}
              textAnchor={
                order === 0 ? "start" : order === axisIndexes.length - 1 && axisIndexes.length > 1 ? "end" : "middle"
              }
            >
              {formatAxis(points[index].time)}
            </text>
          ))}
        </svg>
      ) : null}

      {selectedPoint ? (
        <div className="chart-tooltip" style={{ left: tooltipX, top: 27 }}>
          <span className="chart-tooltip-line">{formatTooltipDate(selectedPoint.time)}</span>
          <span className="chart-tooltip-line">
            <span className="chart-tooltip-dot" style={{ background: tint }} />
            {Math.round(selectedPoint.temp)}°
            {selectedPoint.target !== null ? (
              <>
                <span className="chart-tooltip-dot" style={{ background: "var(--target-line)" }} />
                Target {Math.round(selectedPoint.target)}°
              </>
            ) : null}
            <span className="chart-tooltip-status">{runtimeLabel(selectedPoint)}</span>
          </span>
        </div>
      ) : null}
    </div>
  );
}
